import { useLazyQuery } from '@apollo/client';
import styled from '@emotion/styled';
import type { Prismic } from '@landsbankinn/prismic';
import { device, Row, Segment, useMedia } from '@landsbankinn/ui';
import { LandsbankinnContext } from '@landsbankinn/utils';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useImmerReducer } from 'use-immer';
import type { SmartBookingCustomerType } from '../../../__generated__/globalTypes';
import type {
  availableDatesAndTimes,
  availableDatesAndTimesVariables,
} from '../../graphql/Appointment/__generated__/availableDatesAndTimes';
import type { BookAppointment_bookAppointment_BookAppointmentPayload } from '../../graphql/Appointment/__generated__/BookAppointment';
import { availableDatesAndTimes as query } from '../../graphql/Appointment/availableDatesAndTimes';
import { Img } from '../img/Img';
import type { ActionType } from './reducer';
import {
  AppointmentReducer,
  initialState as initialReducerState,
} from './reducer';
import { tempBookAppointmentImage } from './shared/ImageContainer';
import { CustomerInfo } from './steps/CustomerInfo/CustomerInfo';
import { Intro } from './steps/Intro/Intro';
import { SelectDateAndTime } from './steps/SelectDateAndTime/SelectDateAndTime';
import { SelectService } from './steps/SelectService/SelectService';
import { Success } from './steps/Success/Success';

const StyledRow = styled(Row)<{ withImage: boolean }>`
  overflow: ${(props) => (props.withImage ? 'hidden' : 'initial')};
  background: ${(props) => props.theme.shades.primary2};
  padding: ${(props) => (props.withImage ? '0' : '48px 0px 45px 0px')};
  @media ${device.tablet} {
    padding: ${(props) => (props.withImage ? '0' : '100px 0 52px 0')};
    min-height: 640px;
    max-height: 640px;
  }
`;

const OverflowContainer = styled.div`
  padding-bottom: 200px;
  margin-bottom: -200px;
  overflow: hidden;
`;

interface IProps {
  defaultCustomerType?: SmartBookingCustomerType;
  defaultServiceId?: string;
  image?: Prismic.Image;
  title: string;
  copy: string;
}

export enum AppointmentStep {
  Intro = 'intro',
  SelectService = 'selectService',
  SelectDateAndTime = 'selectDateAndTime',
  CustomerInfo = 'customerInfo',
  InfoText = 'infoText',
  Success = 'success',
}

interface AppointmentContext {
  setCurrentStep?: React.Dispatch<React.SetStateAction<AppointmentStep>>;
  state: typeof initialReducerState;
  dispatch?: React.Dispatch<ActionType>;
}

const AppointmentContext = createContext<AppointmentContext>({
  state: initialReducerState,
});

export function BookAppointment({
  defaultServiceId,
  defaultCustomerType,
  image,
  title,
  copy,
}: IProps) {
  const [currentStep, setCurrentStep] = useState<AppointmentStep>(
    AppointmentStep.Intro,
  );
  const isMobile = useMedia('sm');

  const [successData, setSuccessData] = useState<
    BookAppointment_bookAppointment_BookAppointmentPayload | undefined
  >(undefined);

  const [state, dispatch] = useImmerReducer(
    AppointmentReducer,
    initialReducerState,
  );
  const { client } = useContext(LandsbankinnContext);

  const onCompleted = React.useCallback(() => {
    setCurrentStep(AppointmentStep.SelectDateAndTime);
  }, []);

  const [fetchDatesAndTime, { data, loading }] = useLazyQuery<
    availableDatesAndTimes,
    availableDatesAndTimesVariables
  >(query, {
    client,
    fetchPolicy: 'network-only',
    onCompleted,
  });

  useEffect(() => {
    if (defaultServiceId && defaultCustomerType) {
      dispatch({
        type: 'selectCustomerType',
        customerType: defaultCustomerType,
      });
    }
  }, [defaultServiceId, defaultCustomerType, dispatch]);

  return (
    <AppointmentContext.Provider
      value={{
        state,
        dispatch,
        setCurrentStep,
      }}
    >
      <div id="book-appointment">
        <SegmentContainer>
          <OverflowContainer>
            {isMobile && (
              <Img
                src={image ?? tempBookAppointmentImage}
                aspectRatio={4 / 3}
              />
            )}
            <StyledRow
              gutter={isMobile ? 0 : undefined}
              withImage={
                currentStep === AppointmentStep.Intro ||
                currentStep === AppointmentStep.Success
              }
            >
              {currentStep === AppointmentStep.Intro && (
                <Intro
                  defaultCustomerType={defaultCustomerType}
                  image={image}
                  title={title}
                  copy={copy}
                />
              )}
              {currentStep === AppointmentStep.SelectService && (
                <SelectService
                  defaultServiceId={defaultServiceId}
                  fetchDatesAndTime={fetchDatesAndTime}
                  loadingDateAndTime={loading}
                />
              )}
              {currentStep === AppointmentStep.SelectDateAndTime && (
                <SelectDateAndTime data={data} />
              )}
              {currentStep === AppointmentStep.CustomerInfo && (
                <CustomerInfo setSuccessData={setSuccessData} />
              )}
              {currentStep === AppointmentStep.Success && (
                <Success data={successData} image={image} />
              )}
            </StyledRow>
          </OverflowContainer>
        </SegmentContainer>
      </div>
    </AppointmentContext.Provider>
  );
}

export const useAppointmentContext = () => useContext(AppointmentContext);

const SegmentContainer: React.FC<any> = ({ children }) => {
  const isDesktop = useMedia('lg');
  return !isDesktop ? children : <Segment>{children}</Segment>;
};
